import React, { lazy, Suspense, useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useViewportScroll, motion, useTransform } from "framer-motion";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { scroller } from "react-scroll";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "../../../locales/de.json";
import localEN from "../../../locales/en.json";

const ReactSVGIcon = lazy(() =>
	import(/* webpackChunkName: "reactSVGIcon" */ "@micado-digital/react-svg-icon/ReactSVGIcon")
);
const Button = lazy(() =>
	import(/* webpackChunkName: "reactButton" */ "../../../components/button")
);

const useStyles = makeStyles(theme => ({
	hero: props => ({
		background: "#20303D",
		height: "max(600px, 100vh)",
		minHeight: 800,
		opacity: 0,
		position: "relative",
		transition: "opacity 1000ms",
		[theme.breakpoints.up("sm")]: {
			height: "max(800px, 110vh)"
		},
		[theme.breakpoints.up("md")]: {
			height: "max(900px, 120vh)"
		},
		[theme.breakpoints.up("lg")]: {
			height: "140vh"
		},
		"@media (min-width: 1500px)": {
			height: "150vh",
			minHeight: 1150
		},
		[theme.breakpoints.up("xl")]: {
			height: "165vh"
		},
		"@media (min-width: 1024px) and (min-height: 500px)": {
			minHeight: "1000px"
		},
		"@media (min-width: 1200px) and (min-height: 500px)": {
			minHeight: "1100px"
		},
		"@media (min-width: 1400px) and (min-height: 500px)": {
			minHeight: "1200px"
		},
		"@media (min-width: 1600px) and (min-height: 500px)": {
			minHeight: "1250px"
		},
		"@media (min-width: 1700px) and (min-height: 500px)": {
			minHeight: "1300px"
		},
		"@media (min-width: 1800px) and (min-height: 500px)": {
			minHeight: "1350px"
		},
		"@media (min-width: 1900px) and (min-height: 500px)": {
			minHeight: "1400px"
		},
		"@media (min-width: 1920px) and (min-height: 500px)": {
			minHeight: "1600px"
		},
		"&:before": {
			background: !props?.mode
				? "linear-gradient(180deg, #20373D 0%, rgba(32, 55, 61, 0) 100%)"
				: "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
			content: "''",
			height: "50%",
			left: 0,
			position: "absolute",
			pointerEvents: "none",
			top: 0,
			width: "100%",
			zIndex: 4
		},
		"&:after": {
			background: "linear-gradient(180deg, rgba(32, 48, 61, 0) 54.66%, #20303D 98.41%)",
			bottom: 0,
			content: "''",
			height: "100%",
			left: 0,
			maxHeight: "40vh",
			position: "absolute",
			pointerEvents: "none",
			width: "100%",
			zIndex: 4
		}
	}),
	heroShow: {
		opacity: "1 !important"
	},
	animation: props => ({
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		height: "100%",
		padding: theme.spacing(0, 2),
		position: "relative",
		width: "100%",
		"&:before": {
			backgroundImage: `url('/img/hero/hero-bg-${props?.mode ? "day" : "night"}-large.jpg')`,
			backgroundPosition: "top center",
			backgroundRepeat: "repeat-y",
			backgroundSize: "cover",
			content: "''",
			height: "100%",
			position: "absolute",
			top: 0,
			width: "100%",
			zIndex: 0
		}
	}),
	logo: {
		alignItems: "center",
		display: "flex",
		flexDirection: "column",
		marginTop: 90,
		position: "relative",
		zIndex: 4
	},
	animatedLogo: {
		marginBottom: theme.spacing(2)
	},
	headline: {
		...theme?.customTypography?.hero?.mobile?.hero1,
		marginTop: theme.spacing(6),
		maxWidth: 1100,
		position: "relative",
		zIndex: 10,
		"@media (min-width: 1920px) and (min-height: 1080px)": {
			...theme?.customTypography?.hero?.desktop?.hero1,
			marginTop: theme.spacing(10)
		}
	},
	scrollDown: {
		alignItems: "center",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		marginTop: theme.spacing(2.5),
		position: "relative",
		zIndex: 10,
		"& .button-custom": {
			margin: theme.spacing(2, 0)
		},
		"& > svg": {
			alignItems: "center",
			display: "flex",
			justifyContent: "center",
			margin: "0 auto",
			width: "100%"
		},
		[theme.breakpoints.up("xl")]: {
			marginTop: theme.spacing(5)
		}
	},
	hotel: {
		bottom: 0,
		left: 0,
		position: "absolute",
		pointerEvents: "none",
		right: 0,
		zIndex: 4,
		"& img": {
			display: "block",
			height: "auto",
			width: "100%"
		}
	},
	mountains: {
		bottom: 0,
		left: 0,
		position: "absolute",
		pointerEvents: "none",
		right: 0,
		zIndex: 0,
		"& img": {
			display: "block",
			height: "auto",
			width: "100%"
		}
	}
}));

const Hero = props => {
	const css = useStyles(props);
	const [preloader, setPreloader] = useState(true);
	const theme = useTheme();
	const mediumScreen = useMediaQuery(theme.breakpoints.up("md"));
	const bigScreen = useMediaQuery(theme.breakpoints.up("xl"));
	const { scrollY } = useViewportScroll();
	const mountainsMobileY = useTransform(scrollY, [0, 500], [0, -9]);
	const mountainsTabletY = useTransform(scrollY, [0, 500], [0, -20]);
	const mountainsDesktopY = useTransform(scrollY, [0, 500], [0, -40]);
	const l = useLocals({
		lang: props?.lang,
		res: {
			de: localDE,
			en: localEN
		}
	});

	useEffect(() => {
		setTimeout(() => setPreloader(false), 100);
	}, []);

	// animations

	const animationLogo = { duration: 2, ease: "easeIn", delay: 1.5 };
	const animationLogoText = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				delay: 2.5,
				duration: 2,
				ease: "easeIn"
			}
		}
	};
	const animationHeroText = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				delay: 3,
				duration: 2,
				ease: "easeIn"
			}
		}
	};
	const animationLineTop = { duration: 1, ease: "easeIn", delay: 3.5 };
	const animationButton = { duration: 1, ease: "easeIn", delay: 4.5 };
	const animationLineBottom = { duration: 1, ease: "easeIn", delay: 5 };

	const handleScroll = () => {
		scroller.scrollTo("scroll-start", {
			duration: 800,
			delay: 0,
			offset: -100,
			smooth: "easeOutCubic"
		});
	};

	return (
		<div className={clsx(css.hero, { [css.heroShow]: !preloader })}>
			<div className={clsx("hero__animation", css.animation)}>
				<div className={clsx("hero__animation-logo", css.logo)}>
					<svg
						className={css.animatedLogo}
						width={bigScreen ? 95 : 60}
						height={bigScreen ? 95 : 60}
						viewBox="0 0 512 512"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<motion.path
							initial={{ pathLength: 0, opacity: 0 }}
							animate={{ pathLength: 1, opacity: 1 }}
							fill="transparent"
							transition={animationLogo}
							d="M255.75 256.25L132 77L12 256.5L132 435.5L255.75 256.25ZM255.75 256.25L376 435.5L499.75 256.5L376 77L255.75 256.25ZM135 224L256.748 42L378 224M135 289L256.748 471L378 289"
							stroke="#9F9587"
							strokeWidth="22"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<motion.div initial="hidden" animate="visible" variants={animationLogoText}>
						<Suspense fallback={<></>}>
							<ReactSVGIcon
								size={bigScreen ? 298 : 180}
								color={!props?.mode ? "paper" : "inherit"}
								src={"/img/logo-text.svg"}
							/>
						</Suspense>
					</motion.div>
				</div>
				<div>
					<Typography
						className={css.headline}
						color={!props?.mode ? "textSecondary" : "textPrimary"}
						component="div"
						variant="h1"
					>
						<motion.div initial="hidden" animate="visible" variants={animationHeroText}>
							{l("hero.headline")}
						</motion.div>
					</Typography>
				</div>
				<div className={clsx("hero__animation-scrollDown", css.scrollDown)}>
					<svg
						width="8"
						height={bigScreen ? 80 : 40}
						viewBox="0 0 8 80"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<motion.path
							initial={{ pathLength: 0 }}
							animate={{ pathLength: 1 }}
							fill="transparent"
							transition={animationLineTop}
							d="M4 5V80"
							stroke="#9F9587"
							strokeWidth="2"
						/>
						<motion.path
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={animationLineTop}
							d="M2.13844 4.02157L4.02157 2.13844L5.9047 4.02157L4.02157 5.9047L2.13844 4.02157Z"
							stroke="#9F9587"
							strokeWidth="4"
							strokeLinejoin="round"
						/>
					</svg>
					<Suspense fallback={<></>}>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={animationButton}
						>
							<Suspense fallback={<></>}>
								<Button
									label={l("hero.button")}
									color={!props?.mode ? "#FFFFFF" : theme?.palette?.secondary?.dark}
									colorHover={!props?.mode ? "#FFFFFF" : theme?.palette?.secondary?.dark}
									border={!props?.mode ? "#FFFFFF" : theme?.palette?.secondary?.main}
									borderHover={!props?.mode ? "#FFFFFF" : theme?.palette?.secondary?.dark}
									size={bigScreen ? "large" : "small"}
									onClick={handleScroll}
								/>
							</Suspense>
						</motion.div>
					</Suspense>
					<svg
						width="3"
						height={bigScreen ? 112 : 80}
						viewBox="0 0 3 112"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<motion.path
							initial={{ pathLength: 0 }}
							animate={{ pathLength: 1 }}
							fill="transparent"
							transition={animationLineBottom}
							d="M1.5 0L1.5 112"
							stroke="url(#paint0_linear)"
							strokeWidth="2"
						/>
						<defs>
							<linearGradient
								id="paint0_linear"
								x1="1.00018"
								y1="0"
								x2="1.00018"
								y2="112"
								gradientUnits="userSpaceOnUse"
							>
								<stop stopColor="#9F9587" />
								<stop offset="1" stopColor="#9F9587" stopOpacity="0" />
							</linearGradient>
						</defs>
					</svg>
				</div>
				<div className={clsx("hero__animation-hotel", css.hotel)}>
					<picture>
						<source
							srcSet={`/img/hero/bg-hotel-summer-${
								bigScreen ? "large" : mediumScreen ? "medium" : "small"
							}.webp`}
							type="image/webp"
						></source>
						<source
							srcSet={`/img/hero/bg-hotel-summer-${
								bigScreen ? "large" : mediumScreen ? "medium" : "small"
							}.png`}
							type="image/png"
						></source>
						<img
							src={`/img/hero/bg-hotel-summer-${
								bigScreen ? "large" : mediumScreen ? "medium" : "small"
							}.png`}
							height={bigScreen ? 1709 : mediumScreen ? 1282 : 501}
							width={bigScreen ? 2560 : mediumScreen ? 1920 : 750}
							alt="Kaiserlodge"
						></img>
					</picture>
				</div>
				<motion.div
					className={clsx("hero__animation-mountains", css.mountains)}
					style={{
						y: bigScreen
							? mountainsDesktopY
							: mediumScreen
							? mountainsTabletY
							: mountainsMobileY,
						x: 0
					}}
					transition={{ duration: 4, ease: "linear" }}
				>
					<picture>
						<source
							srcSet={`/img/hero/bg-mountains-summer-${
								bigScreen ? "large" : mediumScreen ? "medium" : "small"
							}.webp`}
							type="image/webp"
						></source>
						<source
							srcSet={`/img/hero/bg-mountains-summer-${
								bigScreen ? "large" : mediumScreen ? "medium" : "small"
							}.png`}
							type="image/png"
						></source>
						<img
							src={`/img/hero/bg-mountains-summer-${
								bigScreen ? "large" : mediumScreen ? "medium" : "small"
							}.png`}
							height={bigScreen ? 1709 : mediumScreen ? 1282 : 501}
							width={bigScreen ? 2560 : mediumScreen ? 1920 : 750}
							alt="Kaiserlodge"
						></img>
					</picture>
				</motion.div>
			</div>
		</div>
	);
};

Hero.propTypes = {
	size: PropTypes.string,
	search: PropTypes.bool
};

export default Hero;
